import { ChangeEvent, useMemo } from "react";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_DECISION_ACTION_TYPE_OPTIONS } from "@constants/policy-decision-action-type-options";
import { LayoutTypes } from "@enums/layout-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import {
    FormattedRpServiceConfigurationPolicyConfig
} from "@idw-types/data/rp-service-configuration";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { sortStrings } from "@utils/string-util";
import { CardContainer } from "@containers/common/card-container/card-container";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { DecisionHeaderContainer } from "../decision-header-container/decision-header-container";
import commonStyles from "../../../../verification-service-details-component.less";
import { getAxnRpServiceOptions, getObligationStepUpToList, isObligationApiKeyCorrect } from "@utils/policies/policy-util";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { AXNUIContentPolicyDecisionRetryMessage } from "@iddataweb/idw-common-ui/types/axnui-content";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";
import Alert from "react-bootstrap/Alert";

type DefaultDecisionContainerProps = {
    isDisabled: boolean;
    isUsingThemedUI: boolean;
    axnRpServices: Array<AxnRpService>;
    policyConfig: FormattedRpServiceConfigurationPolicyConfig;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    validationErrors: PolicyDetailErrors;
    rpServiceID: string;
    workflowID: string;
    axnuiContent: AXNUIContent;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updatePolicyConfig: (policyConfig: FormattedRpServiceConfigurationPolicyConfig) => void;
};

export const DefaultDecisionContainer = (props: DefaultDecisionContainerProps) => {
    const {
        isDisabled,
        isUsingThemedUI,
        axnRpServices,
        validationErrors,
        policyConfig,
        simpleRPServiceWorkflows,
        rpServiceID,
        workflowID,
        axnuiContent,
        updateAXNUIContentPropertyValue,
        updatePolicyConfig
    } = props;


    const handleContentPolicyDecisionRetryMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentPolicyDecisionRetryMessage("default", event.target.value);
    };
    const updateAXNUIContentPolicyDecisionRetryMessage = (key: keyof AXNUIContentPolicyDecisionRetryMessage, value: string) => {
        const defaultContentPolicyRetryMessage = {
            retryMessage: value
        };
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            policyDecisionRetryMessage: {
                ...axnuiContent.policyDecisionRetryMessage,
                [key]: {
                    ...defaultContentPolicyRetryMessage
                }
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };
    const handleDefaultActionChange = (newValue: SelectOption) => {
        updatePolicyConfig({
            ...policyConfig,
            defaultAction: newValue.value as PolicyDecisionActionTypes,
            defaultActionStepUpApiKey: null
        });
    };

    const handleDefaultActionStepUpApiKeyChange = (newValue: SelectOption) => {
        updatePolicyConfig({
            ...policyConfig,
            defaultActionStepUpApiKey: newValue.value
        });
    };

    const handleDefaultActionInlineRetryChange = (value: string) => {
        const newActionRetryValue = isStringTrue(value);
        const newRetries = newActionRetryValue ? "1" : "0";
        const newDecisions = policyConfig.decisions.map(decision => ({
            ...decision,
            retries: decision.policyDecisionType !== PolicyDecisionActionTypes.APPROVE ? newRetries : "0"
        }));

        updatePolicyConfig({
            ...policyConfig,
            policyRetries: newRetries,
            defaultActionRetries: newRetries,
            decisions: newDecisions
        });
    };

    const handleDefaultActionRetryCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newDecisions = policyConfig.decisions.map(decision => ({
            ...decision,
            retries: decision.policyDecisionType !== PolicyDecisionActionTypes.APPROVE && event.target.value ? event.target.value : "0"
        }));
        updatePolicyConfig({
            ...policyConfig,
            policyRetries: event.target.value,
            defaultActionRetries: event.target.value,
            decisions: newDecisions
        });
    };

    const selectedDefaultAction = useMemo(() => {
        return POLICY_DECISION_ACTION_TYPE_OPTIONS.find(option => option.value === policyConfig.defaultAction);
    }, [policyConfig.defaultAction]);

    const obligationStepUpList = useMemo(() => {
        if (axnRpServices.length > 0) {
            return getObligationStepUpToList(rpServiceID, workflowID, axnRpServices, simpleRPServiceWorkflows);
        }
        return [];

    }, [axnRpServices, rpServiceID, workflowID, simpleRPServiceWorkflows]);

    const axnRpServiceOptions = useMemo(() => {
        if (policyConfig.defaultAction === PolicyDecisionActionTypes.OBLIGATION && policyConfig.defaultActionStepUpApiKey) {
            return getAxnRpServiceOptions(obligationStepUpList, policyConfig.defaultActionStepUpApiKey, axnRpServices);
        } else {
            return obligationStepUpList
            .map(service => ({ label: `${service.serviceName} (${service.apiKey})`, value: service.apiKey }))
            .sort((a, b) => sortStrings(a.label, b.label));
        }
       
    }, [obligationStepUpList, policyConfig.defaultAction, policyConfig.defaultActionStepUpApiKey, axnRpServices]);

    const selectedDefaultRuleStepUpApiKey = useMemo(() => {
        if (policyConfig.defaultAction === PolicyDecisionActionTypes.OBLIGATION && policyConfig.defaultActionStepUpApiKey) {
            const serviceOption = axnRpServiceOptions.find(option => option.value === policyConfig.defaultActionStepUpApiKey);
            if (serviceOption) {
                return serviceOption;
            }
        }
        return null;
    }, [axnRpServiceOptions, policyConfig.defaultAction, policyConfig.defaultActionStepUpApiKey]);


    const shouldShowObligationWarningMessage = useMemo(() => {
        if (obligationStepUpList.length && policyConfig.defaultAction === PolicyDecisionActionTypes.OBLIGATION && policyConfig.defaultActionStepUpApiKey) {
            return !isObligationApiKeyCorrect(policyConfig.defaultActionStepUpApiKey, obligationStepUpList);
        }

        return false;
    }, [obligationStepUpList, policyConfig.defaultAction, policyConfig.defaultActionStepUpApiKey]);

    return (
        <CardContainer>
            <DecisionHeaderContainer
                isDisabled={true}
                showDragAndDropIndicatorIcon={false}
                decision={null}
                title={`Default Rule`}
                copyDecision={() => { }}
                copyOppositeDecision={() => { }}
                removeDecision={() => { }}
            />
            <span className={commonStyles.description}>Select the default behavior that occurs if none of the rules above are met</span>
            <SelectFormGroup
                controlId={"policy-config-default-rule-decision-type-select-input"}
                automationId={"policy-config-default-rule-decision-type-select-input"}
                label={"Action"}
                ariaLabel={"Action"}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                layout={LayoutTypes.ROW}
                labelWidth={LabelWidthTypes.SMALL}
                onChange={handleDefaultActionChange}
                options={POLICY_DECISION_ACTION_TYPE_OPTIONS}
                selectedOption={selectedDefaultAction}
            />
            {policyConfig.defaultAction === PolicyDecisionActionTypes.OBLIGATION && (
                <>
                    <SelectFormGroup
                        controlId={"policy-config-default-rule-step-up-api-key-select-input"}
                        automationId={"policy-config-default-rule-step-up-api-key-select-input"}
                        label={resources.modals.addRpServiceConfigurationPolicyConfigDecision.inputs.stepUpTo.label}
                        ariaLabel={resources.modals.addRpServiceConfigurationPolicyConfigDecision.inputs.stepUpTo.label}
                        disabled={isDisabled}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        layout={LayoutTypes.ROW}
                        labelWidth={LabelWidthTypes.SMALL}
                        onChange={handleDefaultActionStepUpApiKeyChange}
                        options={axnRpServiceOptions}
                        selectedOption={selectedDefaultRuleStepUpApiKey}
                    />
                    {shouldShowObligationWarningMessage && <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.policies.sections.details.obligationWarningMessage}</Alert>}
                </>)}
            {[PolicyDecisionActionTypes.DENY, PolicyDecisionActionTypes.OBLIGATION].includes(policyConfig.defaultAction) && (
                <RadioInputFormGroup
                    controlId={"policy-config-default-rule-inline-retry-radio-input"}
                    automationId={"policy-config-default-rule-inline-retry-radio-input"}
                    label={"Inline Retry"}
                    onChange={handleDefaultActionInlineRetryChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    isRequired={true}
                    disabled={isDisabled}
                    value={policyConfig.policyRetries === "0" ? "false" : "true"}
                />
            )}
            {[PolicyDecisionActionTypes.DENY, PolicyDecisionActionTypes.OBLIGATION].includes(policyConfig.defaultAction) && policyConfig.policyRetries !== "0" && (
                <InputFormGroup
                    controlId={"policy-config-default-rule-retry-attempts-input"}
                    automationId={"policy-config-default-rule-retry-attempts-input"}
                    disabled={isDisabled}
                    isRequired={true}
                    label={"Retry Attempts"}
                    ariaLabel={"Retry Attempts"}
                    value={policyConfig.defaultActionRetries}
                    type={"number"}
                    onChange={handleDefaultActionRetryCountChange}
                />
            )}
            {policyConfig.defaultAction === PolicyDecisionActionTypes.DENY && isUsingThemedUI && policyConfig.policyRetries !== "0" && (
                <>
                <InputFormGroup
                    controlId={"policy-config-default-rule-retry-message-input"}
                    automationId={"policy-config-default-rule-retry-message-input"}
                    disabled={isDisabled}
                    isRequired={true}
                    label={"Retry Message"}
                    ariaLabel={"Retry Message"}
                    type={"text"}
                    value={axnuiContent.policyDecisionRetryMessage["default"].retryMessage}
                    onChange={handleContentPolicyDecisionRetryMessageChange}
                />
                {validationErrors.policyConfigDecisionsRetryErrors['default'] || null}
                </>
            )}
        </CardContainer>
    );
};